import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { useUpdateTableTabMutation } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DetailedTableTab } from '~/components/tables/types'
import { Button } from '~/components/ui/atoms/button/Button'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { useDossSnackbar } from '../DossSnackbar'

interface RenameTableTabDialogProps {
  handleTabMenuClose: () => void
  setTabToUpdate: (tab: null | DetailedTableTab) => void
  tabToUpdate: DetailedTableTab
  orderedTableTabs: DetailedTableTab[]
  tableName: string
}

/**
 * Renders the dialog to update a table tab's name
 */
export const RenameTableTabDialog = ({
  handleTabMenuClose,
  setTabToUpdate,
  tabToUpdate,
  orderedTableTabs,
  tableName,
}: RenameTableTabDialogProps) => {
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const [updatedTabName, setUpdatedTabName] = useState<string>('')
  const [updateTableTab] = useUpdateTableTabMutation()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleUpdateTableTabName = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    // Check if the updatedTabName already exists
    const isNameDuplicate = orderedTableTabs.some((tab) => tab.name === updatedTabName)
    if (isNameDuplicate) {
      setErrorMessage(t('table_tab.rename.duplicate_name'))
      return
    }

    await updateTableTab({
      variables: { input: { tableTabId: tabToUpdate.id, name: updatedTabName } },
    })
      .then(() => {
        snackbar.showInfo(t('table_tab.rename.success', { updatedTabName }))
        handleTabMenuClose()
        setTabToUpdate(null)
      })
      .catch(() => {
        snackbar.showError(t('table_tab.rename.error'))
      })
  }

  useEffect(() => {
    setUpdatedTabName(tabToUpdate.name)
  }, [tabToUpdate])

  const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setTabToUpdate(null)
    handleTabMenuClose()
  }

  return (
    <Modal
      actions={
        <Button
          disabled={tabToUpdate.name === updatedTabName || isEmpty(updatedTabName)}
          onClick={handleUpdateTableTabName}
          variant="secondary"
        >
          {t('common.actions.save')}
        </Button>
      }
      disableRestoreFocus
      onClose={handleCancelClick}
      open
      title={t('table_tab.rename.modal_title', { tableName })}
    >
      <ShortTextInput
        autoFocus
        errorText={errorMessage || undefined}
        onChange={(e) => {
          setUpdatedTabName(e.target.value)
          setErrorMessage(null)
        }}
        onClick={(event) => event.stopPropagation()}
        value={updatedTabName}
      />
    </Modal>
  )
}
